import React from "react"
import {
  parseColorPalette,
  translateCreativity,
  translateDecorStyle,
  translateRoomType,
  translateTheme,
} from "./generationPresetDisplay"

/**
 * Presets for a Furniture Design generation (ai_feature_detail list rows).
 * The two steps carry different fields — `furniture_piece` describes the piece
 * itself (category / style / materials / color), `furniture_room` describes the
 * room it was placed into — so a row is shown when the backend sent that field
 * for this step (even as null, rendered "—") and omitted when it sent nothing.
 */

/** "Single Bed" from a string, ["Wood","Oak"] from a list, "" from anything empty. */
const toText = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => toText(item))
      .filter(Boolean)
      .join(", ")
  }
  if (value == null) return ""
  if (typeof value === "object") return ""
  const text = String(value).trim()
  return text
}

/** { width: 120, height: 90 } → "Width 120 · Height 90" */
const formatDimensions = (dimensions) => {
  if (!dimensions || typeof dimensions !== "object" || Array.isArray(dimensions)) {
    return ""
  }
  return Object.entries(dimensions)
    .map(([key, value]) => {
      const text = toText(value)
      if (!text) return ""
      const label = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      return `${label} ${text}`
    })
    .filter(Boolean)
    .join(" · ")
}

export const EMPTY_PRESET_VALUE = "—"

/** Did the backend send this field for this step, whatever its value? */
const hasField = (source, keys) =>
  Boolean(source) &&
  typeof source === "object" &&
  keys.some((key) => Object.prototype.hasOwnProperty.call(source, key))

export const getFurnitureDesignPresetRows = (gen, lang = "en") => {
  const presets = gen?.presets && typeof gen.presets === "object" ? gen.presets : {}
  const isAr = lang === "ar"

  const roomType = toText(gen?.room_type || presets.room_type)
  const rows = [
    {
      label: isAr ? "نوع الغرفة:" : "Room Type:",
      keys: ["room_type"],
      value: roomType ? translateRoomType(roomType, lang) : "",
    },
    {
      label: isAr ? "الفئة:" : "Category:",
      keys: ["category"],
      value: toText(gen?.category || presets.category),
    },
    {
      label: isAr ? "النوع الفرعي:" : "Subtype:",
      keys: ["subtype"],
      value: toText(gen?.subtype || presets.subtype),
    },
    {
      label: isAr ? "النمط:" : "Style:",
      keys: ["style", "decor_style"],
      value: gen?.style
        ? toText(gen.style)
        : gen?.decor_style
        ? translateDecorStyle(gen.decor_style, lang)
        : toText(presets.style),
    },
    {
      label: isAr ? "الخامات:" : "Materials:",
      keys: ["material", "materials"],
      value: toText(gen?.material || presets.materials),
    },
    {
      label: isAr ? "اللون:" : "Color:",
      keys: ["colour", "color"],
      value: toText(gen?.colour || gen?.color || presets.color?.solid_color_ids),
    },
    {
      label: isAr ? "المصمم:" : "Designer:",
      keys: ["designer"],
      value: toText(gen?.designer),
    },
    {
      label: isAr ? "النقشة:" : "Pattern:",
      keys: ["pattern"],
      value: toText(gen?.pattern),
    },
    {
      label: isAr ? "الشكل:" : "Shape:",
      keys: ["shape"],
      value: toText(gen?.shape),
    },
    {
      label: isAr ? "الأبعاد:" : "Dimensions:",
      keys: ["dimensions"],
      value: formatDimensions(gen?.dimensions),
    },
    {
      label: isAr ? "المميزات:" : "Features:",
      keys: ["features"],
      value: toText(gen?.features),
    },
    {
      label: isAr ? "النمط العام:" : "Theme:",
      keys: ["room_theme", "theme"],
      value: gen?.room_theme ? translateTheme(gen.room_theme, lang) : "",
    },
    {
      label: isAr ? "الأسلوب الإبداعي:" : "Creativity:",
      keys: ["creativity"],
      value: gen?.creativity ? translateCreativity(gen.creativity, lang) : "",
    },
  ]

  return rows
    .filter((row) => row.value || hasField(gen, row.keys) || hasField(presets, row.keys))
    .map((row) => ({
      label: row.label,
      value: row.value || EMPTY_PRESET_VALUE,
    }))
}

const FurnitureDesignPresetsFooter = ({ gen, lang = "en" }) => {
  const rows = getFurnitureDesignPresetRows(gen, lang)
  const colors = parseColorPalette(gen?.color_palette)
  const showPalette =
    colors.length > 0 ||
    Object.prototype.hasOwnProperty.call(gen || {}, "color_palette")
  if (rows.length === 0 && !showPalette) return null

  return (
    <div className="sketch-footer furniture-presets-footer">
      {rows.map((row) => (
        <div key={row.label}>
          <div style={{ color: "#666", fontSize: "14px" }}>{row.label}</div>
          <div>{row.value}</div>
        </div>
      ))}

      {showPalette ? (
        <div>
          <div
            style={{
              color: "#666",
              fontSize: "14px",
              // Swatches need breathing room under the label; a plain "—" sits
              // on the same baseline as the other values without it.
              marginBottom: colors.length > 0 ? "8px" : 0,
            }}
          >
            {lang === "ar" ? "لوحة الألوان:" : "Color Palette:"}
          </div>
          {colors.length === 0 ? (
            <div>{EMPTY_PRESET_VALUE}</div>
          ) : (
            <div style={{ display: "flex", gap: "8px" }}>
              {colors.map((color) => (
                <div
                  key={color}
                  style={{
                    width: "20px",
                    height: "10px",
                    borderRadius: "4px",
                    backgroundColor: color,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default FurnitureDesignPresetsFooter
